import { favoritesRemove, favoritesAdd, favoritesCheck } from '@/api/favorites.js'
import { getMallAppToken } from '@/utils/globalData.js'
import { get } from 'lodash-es'

import { ElMessage } from 'element-plus'
import { computed, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue'
import { useMainStore } from './store.js'


export function useCollect(itemId, options = {}) {
  const hasCollect = ref(false)
  const getItemId = () => unref(itemId)
  let readyCheckTimer = null
  let readyCheckRetry = 0
  const store = useMainStore()
  const platformType = options?.platformType || 'tbGlobal'
  const collectType = Number(options?.collectType ?? 1)

  const memberCode = computed(() => {
    const mc = get(store, 'mallUserInfo.memberCode') || get(store, 'userInfo.memberCode') || ''
    return String(mc || '').trim()
  })

  function getItemMeta() {
    const titleRef = unref(options?.itemTitle)
    const picRef = unref(options?.itemPic)
    const shopNameRef = unref(options?.itemShopName)
    const shopIdRef = unref(options?.itemShopId)
    const priceRef = unref(options?.itemPrice)
    const priceNum = Number(priceRef)
    return {
      itemTitle: String(titleRef || '').trim(),
      itemPic: String(picRef || '').trim(),
      itemShopName: String(shopNameRef || '').trim(),
      itemShopId: shopIdRef == null ? '' : String(shopIdRef).trim(),
      itemPrice: Number.isFinite(priceNum) ? priceNum : undefined,
    }
  }

  function getBasePayload() {
    const currentItemId = getItemId()
    let itemId = ''
    if (currentItemId != null && currentItemId !== '') {
      if (typeof currentItemId === 'number' && Number.isFinite(currentItemId)) {
        itemId = currentItemId
      }
      else {
        const s = String(currentItemId).trim()
        const n = Number(s)
        itemId = Number.isFinite(n) && s === String(n) ? n : s
      }
    }
    return {
      memberCode: memberCode.value,
      platformType,
      itemId,
      collectType,
      appType: 'react-mall-app',
    }
  }

  async function addToCollect() {
    if (!memberCode.value) {
      ElMessage.warning('请先登录')
      return
    }
    const itemMeta = getItemMeta()
    const payload = {
      ...getBasePayload(),
      ...itemMeta,
    }
    const res = await favoritesAdd({
      ...payload,
    })
    if (res.success) {
      ElMessage({
        message:$t('messages.collectSuccess') ,
        type: 'success',
        plain: true,
      })
      hasCollect.value = true
    }
  }

  async function cancelCollect() {
    if (!memberCode.value) {
      ElMessage.warning('请先登录')
      return
    }
    const res = await favoritesRemove({
      ...getBasePayload(),
    })
    if (res.success) {
      ElMessage({
        message:$t('messages.cancelCollectSuccess') ,
        type: 'success',
        plain: true,
      })
      hasCollect.value = false
    }
  }

  async function queryHasCollect() {
    if (!process.client) return
    if (!getMallAppToken()) {
      hasCollect.value = false
      return
    }
    if (!memberCode.value) {
      hasCollect.value = false
      return
    }
    if (!String(getItemId() || '').trim()) {
      hasCollect.value = false
      return
    }
    const res = await favoritesCheck({
      ...getBasePayload(),
    })
    hasCollect.value = !!(
      get(res, 'data.isFavorited')
      || get(res, 'data.data.isFavorited')
      || get(res, 'data.favorited')
      || get(res, 'data.data.favorited')
      || Number(get(res, 'data')) > 0
    )
  }

  if (process.client) {
    watch(
      () => [String(getItemId() || '').trim(), memberCode.value, getMallAppToken()],
      () => {
        queryHasCollect()
      },
      { immediate: true },
    )
  }

  onMounted(() => {
    // mall token 可能在页面加载后才写入，做短时补偿检查避免漏掉首次收藏态查询
    readyCheckRetry = 0
    if (readyCheckTimer) {
      clearInterval(readyCheckTimer)
      readyCheckTimer = null
    }
    readyCheckTimer = setInterval(() => {
      readyCheckRetry += 1
      const isReady = !!(
        String(getItemId() || '').trim()
        && String(memberCode.value || '').trim()
        && getMallAppToken()
      )
      if (isReady) {
        queryHasCollect()
        clearInterval(readyCheckTimer)
        readyCheckTimer = null
        return
      }
      if (readyCheckRetry >= 12) {
        clearInterval(readyCheckTimer)
        readyCheckTimer = null
      }
    }, 500)
  })

  onBeforeUnmount(() => {
    if (readyCheckTimer) {
      clearInterval(readyCheckTimer)
      readyCheckTimer = null
    }
  })

  return {
    hasCollect,
    addToCollect,
    cancelCollect,
    queryHasCollect,
  }
}
